import type ExcelJS from 'exceljs';

/**
 * Files the app builds in the browser and hands to the user: the Purchases
 * page's Excel template and the products export (bos-app#120).
 *
 * `downloadBlob` is a side effect, so it lives here as a named adapter rather
 * than beside either feature (rulebook §2). `xlsxBlobOf` is split from it so a
 * caller can keep the Blob and serve it again.
 */

/** The media type of an `.xlsx` workbook. */
export const XLSX_MEDIA_TYPE =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Serializes a workbook into a downloadable `.xlsx` Blob. */
export const xlsxBlobOf = async (workbook: ExcelJS.Workbook): Promise<Blob> => {
    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], { type: XLSX_MEDIA_TYPE });
};

/**
 * Hands a Blob to the browser's own download, by clicking a temporary
 * `<a download>` pointed at an object URL.
 *
 * Bails out when there is no `document`, so it stays callable outside a
 * browser (tests, a future server render).
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
    if (typeof document === 'undefined') {
        return;
    }

    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    // Has to be in the DOM for Firefox to honour the click.
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
